import React from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface SnackbarObjInterface {
  open: boolean;
  message: string;
  severity: "success" | "error" | "warning" | "info";
}

interface SnackbarContextInterface {
  setSnackbarDetail: (snackbarDetail: SnackbarObjInterface) => void;
}
const SnackbarContext = React.createContext<SnackbarContextInterface | null>(
  null
);

interface Props {
  children: React.ReactNode;
}

const SnackbarProvider: React.FC<Props> = ({ children }) => {
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [message, setMessage] = React.useState("false");
  const [severity, setSeverity] = React.useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackbar(false);
  };
  function setSnackbarDetail(snackbarObj: SnackbarObjInterface) {
    const { open, message, severity } = snackbarObj;
    setSeverity(severity);
    setMessage(message);
    setOpenSnackbar(open);
  }

  return (
    <SnackbarContext.Provider
      value={{
        setSnackbarDetail,
      }}
    >
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;

export function useSnackbar() {
  const context = React.useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error(`useSnackbar must be used within a SnackbarProvider`);
  }
  return context;
}
